import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Menu, X, ShoppingCart, User, MessageSquare, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { API_URL } from '@/config/api';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const { user, logout, isAdmin } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_URL}/api/settings`)
      .then(res => res.json())
      .then(data => setSettings(data))
      .catch(console.error);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const logoUrl = settings?.logo_url || 'https://gc3h.co.za/wp-content/uploads/2022/02/gallagher-convention-centre-catering.png';

  return (
    <nav className="bg-white border-b border-border sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" data-testid="nav-logo">
            <img src={logoUrl} alt="GC3H" className="h-14 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/menu" className="text-secondary-900 hover:text-primary transition-colors font-medium" data-testid="nav-menu">
              Menu
            </Link>
            <Link to="/reviews" className="text-secondary-900 hover:text-primary transition-colors font-medium" data-testid="nav-reviews">
              Reviews
            </Link>
            {user && (
              <Link to="/orders" className="text-secondary-900 hover:text-primary transition-colors font-medium" data-testid="nav-orders">
                My Orders
              </Link>
            )}
            {user && (
              <Link to="/chat" className="text-secondary-900 hover:text-primary transition-colors font-medium" data-testid="nav-chat">
                <MessageSquare className="h-5 w-5 inline mr-1" />
                Chat
              </Link>
            )}
          </div>

          {/* Right Side */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Cart */}
            <Link to="/cart" className="relative p-2 hover:bg-muted rounded-md transition-colors" data-testid="nav-cart">
              <ShoppingCart className="h-6 w-6 text-secondary-900" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-2" data-testid="nav-user-menu">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full" />
                    ) : (
                      <User className="h-5 w-5" />
                    )}
                    <span className="font-medium">{user.name?.split(' ')[0]}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/profile" data-testid="nav-profile">Profile</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/orders" data-testid="nav-my-orders">My Orders</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/food-safety" data-testid="nav-food-safety">Food Safety</Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="text-primary font-medium" data-testid="nav-admin">Admin Portal</Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="nav-logout">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/login">
                  <Button variant="ghost" data-testid="nav-login">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-primary text-white hover:bg-primary-600" data-testid="nav-register">Register</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="nav-mobile-toggle"
          >
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <div className="flex flex-col space-y-4">
              <Link to="/menu" className="text-secondary-900 hover:text-primary font-medium" onClick={() => setIsOpen(false)}>
                Menu
              </Link>
              <Link to="/reviews" className="text-secondary-900 hover:text-primary font-medium" onClick={() => setIsOpen(false)}>
                Reviews
              </Link>
              <Link to="/cart" className="text-secondary-900 hover:text-primary font-medium flex items-center" onClick={() => setIsOpen(false)}>
                Cart {itemCount > 0 && <span className="ml-2 bg-primary text-white text-xs px-2 py-0.5 rounded-full">{itemCount}</span>}
              </Link>
              {user ? (
                <>
                  <Link to="/orders" className="text-secondary-900 hover:text-primary font-medium" onClick={() => setIsOpen(false)}>
                    My Orders
                  </Link>
                  <Link to="/chat" className="text-secondary-900 hover:text-primary font-medium" onClick={() => setIsOpen(false)}>
                    Chat
                  </Link>
                  <Link to="/profile" className="text-secondary-900 hover:text-primary font-medium" onClick={() => setIsOpen(false)}>
                    Profile
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" className="text-primary font-medium" onClick={() => setIsOpen(false)}>
                      Admin Portal
                    </Link>
                  )}
                  <button onClick={handleLogout} className="text-left text-destructive font-medium">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-secondary-900 hover:text-primary font-medium" onClick={() => setIsOpen(false)}>
                    Sign In
                  </Link>
                  <Link to="/register" className="text-primary font-medium" onClick={() => setIsOpen(false)}>
                    Register
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
